import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { QRCodeSVG as QRCode } from "qrcode.react";
import { Helmet } from "react-helmet-async";

// Layouts
import FullPageWithHeader from "layouts/FullPageWithHeader";

// Components
import {
  Button,
  FlexBox,
  InputField,
  Loader,
  Link,
  SupportingMessage,
  TextLabel,
} from "@octopus-investments/deep-sea-components";
import LoggingOutPage from "pages/LoggingOutPage/LoggingOutPage";

// Assets
import { ArrowLeft, Clipboard } from "@octopus-investments/deep-sea-components/Icons";

// Actions
import { callIdentify, enableTwoFactor, getQRCode } from "store/slices/accountSlice";
import {
  setEnableTwoFactorLoader,
  setTwoFactorRegisterButtonLoader,
} from "store/slices/loadersSlice";

// Tools
import { MFA_AUTH_CODE_TOOLTIP, VALIDATION_ERRORS } from "utils/constants";

// Tracking
import { ampli } from "ampli";

// Styles
import "./TwoFactorRegisterPage.scss";

export default function TwoFactorRegisterPage() {
  const [authAppUri, setAuthAppUri] = useState("");
  const [conNbr, setConNbr] = useState("");
  const [email, setEmail] = useState("");
  const [logout, setLogout] = useState(false);
  const [passKey, setPassKey] = useState("");
  const [role, setRole] = useState("");
  const [showSystemError, setShowSystemError] = useState(false);
  const [showValidationError, setShowValidationError] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");

  const { isEnableTwoFactorLoader, isTwoFactorRegisterButtonLoader } = useSelector(
    ({ loaders }) => loaders,
  );

  // Redux
  const reduxDispatch = useDispatch();

  const handleAuthAppLink = external_link_clicked => {
    ampli.externalLinkClicked({
      external_link_clicked,
      location: "mfa_register_for_mfa",
      user_id: conNbr,
      user_type: role,
    });
  };

  const handleBackToConsumerLink = () => {
    ampli.contentNavClicked({
      location: "mfa_register_for_mfa",
      navigation_type: "Back to my account",
      user_id: conNbr,
      user_type: role,
    });

    ampli.contentNavClicked({
      location: "mfa_register_error_page",
      navigation_type: "Back to my account",
      user_id: conNbr,
      user_type: role,
    });

    window.location.href = `${process.env.REACT_APP_WELCOME_BTN_URL}account?tab=security`;
  };

  const handleTryAgainButton = () => {
    ampli.contentNavClicked({
      location: "mfa_register_error_page",
      navigation_type: "Try again",
      user_id: conNbr,
      user_type: role,
    });
    setVerificationCode("");
    setShowSystemError(false);
  };

  const handleAuthCodeTooltip = () => {
    ampli.tooltipViewed({
      location: "mfa_register_for_mfa",
      tooltip_type: "authentication_code",
      user_id: conNbr,
      user_type: role,
    });
  };

  const handleCopyToClipboard = text => {
    ampli.mfaPasskeyCopied({ user_id: conNbr, user_type: role });

    navigator.clipboard.writeText(text).then(
      () => {
        console.info("Copied to clipboard successfully!");
      },
      err => {
        console.error("Failed to copy text to clipboard", err);
      },
    );
  };

  const MICROSOFT_AUTHENTICATOR = (
    <Link
      isInline
      href="https://www.microsoft.com/en-gb/security/mobile-authenticator-app"
      onLinkClick={() => handleAuthAppLink("mfa_microsoft")}
      target="_blank"
    >
      Microsoft Authenticator
    </Link>
  );
  const GOOGLE_AUTHENTICATOR = (
    <Link
      isInline
      href="https://support.google.com/accounts/answer/1066447?hl=en&co=GENIE.Platform%3DAndroid"
      onLinkClick={() => handleAuthAppLink("mfa_google")}
      target="_blank"
    >
      Google Authenticator
    </Link>
  );

  const APPLE_AUTHENTICATOR = (
    <Link
      isInline
      href="https://apps.apple.com/gb/app/passwords/id6473799789"
      onLinkClick={() => handleAuthAppLink("mfa_apple")}
      target="_blank"
    >
      Apple Passwords
    </Link>
  );

  const renderBackToConsumerButton = () => (
    <Link
      className="back-button"
      id="backToConsumerButton"
      onLinkClick={() => handleBackToConsumerLink()}
      startIcon={<ArrowLeft />}
    >
      Back
    </Link>
  );

  function handleVerificationCodeChange(e) {
    setVerificationCode(e.value);
    setShowValidationError(showValidationError ? false : showValidationError);
  }

  function handleVerificationCodeKeyPress(e) {
    if (e.which === 13) handleVerifyButton();
  }

  const applyDelay = async (startTime, latency) => {
    const elapsedTime = Date.now() - startTime;
    const remainingTime = latency - elapsedTime;

    if (remainingTime > 0) {
      reduxDispatch(setEnableTwoFactorLoader(true));
      await new Promise(resolve => setTimeout(resolve, remainingTime));
    }
  };

  const handleVerifyButton = async () => {
    const LATENCY = 5000;
    ampli.mfaVerifyClicked({ user_id: conNbr, user_type: role });

    if (isEnableTwoFactorLoader) return;

    if (verificationCode === "") {
      return setShowValidationError(true);
    }

    reduxDispatch(setEnableTwoFactorLoader(true));

    const startTime = Date.now();
    const result = await reduxDispatch(enableTwoFactor({ email, verificationCode }));
    const { success } = result.payload;

    await applyDelay(startTime, LATENCY);

    if (success) {
      ampli.mfaRegistrationCompleted({ user_id: conNbr, user_type: role });
      setLogout(true);
    } else {
      ampli.mfaRegistrationFailed({ user_id: conNbr, user_type: role });
      setShowValidationError(true);
    }
    return reduxDispatch(setEnableTwoFactorLoader(false));
  };

  useEffect(() => {
    reduxDispatch(setTwoFactorRegisterButtonLoader(true));
    reduxDispatch(callIdentify()).then(meResult => {
      const { email, contact, ampliUserType, twoFactorEnabled } = meResult.payload;

      if (twoFactorEnabled) {
        globalThis.location.href = `${process.env.REACT_APP_WELCOME_BTN_URL}account?tab=security`;
        return;
      }
      setEmail(email);
      setConNbr(contact);
      setRole(ampliUserType);

      reduxDispatch(getQRCode({ email })).then(codeResult => {
        const { authAppUri, authenticatorKey, success } = codeResult.payload;

        if (success) {
          setPassKey(authenticatorKey);
          setAuthAppUri(authAppUri);
          reduxDispatch(setTwoFactorRegisterButtonLoader(false));
        } else {
          setShowSystemError(true);
          return reduxDispatch(setTwoFactorRegisterButtonLoader(false));
        }
      });
    });
  }, []);

  if (isEnableTwoFactorLoader) {
    return (
      <FullPageWithHeader>
        <Helmet>
          <title>Octopus Investments Portal | MFA</title>
        </Helmet>
        <div className="two-factor-register-content">
          <FlexBox className="wrapper" container gap="md" mb={1}>
            <FlexBox
              className="loading-wrapper"
              columns={12}
              cs={{ display: "flex", justifyContent: "center" }}
              direction="column"
              align={"center"}
              item
            >
              <Loader customSize="3.75" />
              <h2 className="coheadline">Loading...</h2>
              <p>Please do not close or reload this page.</p>
              <p>
                This page will automatically refresh and you will be taken back to the login page.
                Verification will be complete once you have logged in.
              </p>
            </FlexBox>
          </FlexBox>
        </div>
      </FullPageWithHeader>
    );
  }

  return logout ? (
    <LoggingOutPage isTwoFactorRegisterPage={true} />
  ) : (
    <FullPageWithHeader>
      <Helmet>
        <title>Octopus Investments Portal | MFA</title>
      </Helmet>
      <div className="two-factor-register-content">
        {renderBackToConsumerButton()}
        {showSystemError ? (
          <FlexBox className="wrapper" container gap="md" mb={1}>
            <FlexBox
              className="error-wrapper"
              columns={12}
              cs={{ display: "flex", justifyContent: "center" }}
              direction="column"
              align={"center"}
              item
            >
              <strong>Error</strong>
              <p>
                Unfortunately, there was an error registering you for Multi-Factor Authentication.
              </p>
              <Button onClick={() => handleTryAgainButton()} label="Try again" variant="solid" />
            </FlexBox>
          </FlexBox>
        ) : (
          <>
            <h1 className="coheadline navyBlue">Register for Multi-Factor Authentication</h1>

            <FlexBox className="wrapper" container gap="md" mb={1}>
              <FlexBox columns={12} item>
                <div className="step-wrapper">
                  <div className="badge">1</div>
                  <span>
                    Please ensure you have a multi-factor authentication app installed on your
                    phone, such as {MICROSOFT_AUTHENTICATOR}, {GOOGLE_AUTHENTICATOR} or{" "}
                    {APPLE_AUTHENTICATOR}.
                  </span>
                </div>
              </FlexBox>
              <FlexBox
                columns={12}
                cs={{ display: "flex" }}
                direction="column"
                align={"center"}
                item
              >
                <div className="step-wrapper">
                  <div className="badge">2</div>
                  <span>
                    In your chosen authentication app, scan the QR code below, and follow the steps.
                  </span>
                </div>
                <div className="qr-code">
                  {isTwoFactorRegisterButtonLoader ? (
                    <Loader size="md" />
                  ) : (
                    <QRCode value={authAppUri} size={120} />
                  )}
                </div>
                <div className="passkey-label">
                  <p>Or enter the following passkey in your authentication app:</p>
                </div>
                <InputField
                  autoComplete="off"
                  className="passkey-value"
                  value={passKey}
                  disabled={isTwoFactorRegisterButtonLoader}
                  icon={
                    <button onClick={() => handleCopyToClipboard(passKey)}>
                      <Clipboard />
                    </button>
                  }
                  iconPosition="right"
                  label="Passkey"
                  name="passkey"
                  placeholder="Loading..."
                  type="text"
                  readOnly
                />
              </FlexBox>
              <FlexBox columns={12} item>
                <div className="step-wrapper">
                  <div className="badge">3</div>
                  <span>Enter the 6 digit code provided in the box below: </span>
                </div>
                <TextLabel
                  label="Verification code"
                  text=" "
                  tooltipProps={{
                    content: MFA_AUTH_CODE_TOOLTIP,
                    onTooltipVisible: () => handleAuthCodeTooltip(),
                    position: "top",
                  }}
                />
                <div className="input-button-wrapper">
                  <InputField
                    autoComplete="off"
                    autoFocus
                    name="authenticationCode"
                    className="authentication-code"
                    placeholder="e.g. 123456"
                    onChange={handleVerificationCodeChange}
                    onKeyPress={handleVerificationCodeKeyPress}
                    type="text"
                  />
                  <Button
                    isLoading={isEnableTwoFactorLoader}
                    onClick={handleVerifyButton}
                    label="Verify"
                    variant="solid"
                  />
                </div>
                {showValidationError && (
                  <SupportingMessage status="error" message={VALIDATION_ERRORS["wrongCode"]} />
                )}
              </FlexBox>
            </FlexBox>
          </>
        )}
      </div>
    </FullPageWithHeader>
  );
}
